<script setup>
/**
 * FormControlAccountSettingsReorderScripts
 * Utility component providing maintenance actions like resetting display orders,
 * auditing file usage, and purging system caches.
 */
import {computed, onMounted, getCurrentInstance} from 'vue'
import Button from "../Button.vue"

const props = defineProps({
    field: {
        type: Object,
        required: true
    }
})

const {proxy} = getCurrentInstance()
const store = proxy.$store

// --- Computed ---

const isAdmin = computed(() => store.state.user.user.administrator)

// --- Methods ---

/**
 * Helper to toggle global wait cursor during async operations
 */
const setWaitCursor = (isWaiting) => {
    document.body.style.cursor = isWaiting ? 'wait' : 'initial'
}

const resetDisplayOrders = async () => {
    setWaitCursor(true)
    try {
        await store.dispatch('request/get', {url: 'api/reset-all-display-orders'})
        store.dispatch('toasts/add', {
            title: 'Reset display orders',
            body: '<p>Complete!</p><strong>Please refresh your browser.</strong>'
        })
    } finally {
        setWaitCursor(false)
    }
}

const resetFileUsage = async () => {
    setWaitCursor(true)
    try {
        await store.dispatch('request/get', {url: 'api/reset-file-usage'})
        store.dispatch('toasts/add', {
            title: 'Reset file usage',
            body: '<p>Complete!</p><strong>Please refresh your browser.</strong>'
        })
    } finally {
        setWaitCursor(false)
    }
}

const purgeCache = () => {
    store.dispatch('modals/show', {
        componentName: 'ConfirmModal',
        obj: {
            modalTitle: 'Are you sure?',
            modalBody: `
                <div class="alert alert-danger">
                    This will negatively impact the performance of the site until the caches are rebuilt.
                </div>
                <p>This action should be a last resort.</p>
            `,
            onConfirm: async () => {
                setWaitCursor(true)
                try {
                    await store.dispatch('request/get', {url: 'api/purge-cache'})
                    setWaitCursor(false)

                    const notification = await store.dispatch('toasts/notification', {
                        title: 'Clear the cache',
                        message: 'Starting&hellip;'
                    })

                    store.dispatch('clearCache', {
                        notificationId: notification.notificationId
                    })
                } catch (err) {
                    setWaitCursor(false)
                    console.error('Purge cache failed:', err)
                }
            }
        }
    })
}

// --- Lifecycle ---

onMounted(() => {
    // Setting the label for the form-group container
    props.field.label = 'Reset'
})
</script>

<template>
    <div class="d-flex gap-2">
        <Button class="border" @click.native="resetDisplayOrders">Display orders</Button>
        <Button class="border" @click.native="resetFileUsage">File usage</Button>
        <Button class="border" v-if="isAdmin" @click.native="purgeCache">Purge cache</Button>
    </div>
</template>

<style scoped>
/* Added a small gap utility for the buttons */
.gap-2 {
    gap: 0.5rem;
}
</style>